/**
 * Downloads Screen
 *
 * Provides individual and bulk downloads for all analysis outputs, master data,
 * and the executive report. Files can be downloaded as CSV, XLSX, or Parquet.
 */
import { useState } from "react";
import { useSelector } from "react-redux";
import { downloadFile, downloadXlsx, downloadPptx } from "System/system";

// All downloadable analysis files
const ANALYSIS_FILES = [
  { key: "hasAging", filename: "aging.parquet.gzip", label: "Aging", icon: "fa-duotone fa-timeline" },
  { key: "hasRollforward", filename: "rollforward.parquet.gzip", label: "Rollforward", icon: "fa-duotone fa-code-compare" },
  { key: "hasDelinquencies", filename: "receivables_delinquencies.parquet.gzip", label: "Delinquencies", icon: "fa-duotone fa-triangle-exclamation" },
  { key: "hasDilution", filename: "receivables_dilution.parquet.gzip", label: "Dilution", icon: "fa-duotone fa-droplet" },
  { key: "hasBuyerParent", filename: "buyer_parent.parquet.gzip", label: "Accounts Receivable", icon: "fa-duotone fa-file-invoice-dollar" },
  { key: "hasConcentrationLimits", filename: "concentration_limits.parquet.gzip", label: "Concentration Limits", icon: "fa-duotone fa-chart-pie" },
  { key: "hasNerb", filename: "nerb.parquet.gzip", label: "NERB", icon: "fa-duotone fa-chart-line" },
];

const MASTER_FILES = [
  { key: "hasMaster", filename: "master.parquet.gzip", label: "Raw Master" },
  { key: "hasFiltered", filename: "filtered.parquet.gzip", label: "Filtered Master" },
];

const FORMAT_META = {
  csv: { icon: "fa-duotone fa-file-csv", color: "#3DB887", label: "CSV" },
  xlsx: { icon: "fa-duotone fa-file-excel", color: "#1B7A4A", label: "XLSX" },
  parquet: { icon: "fa-duotone fa-database", color: "#79589F", label: "Parquet" },
};

// Small download button for a single file+format
const DlBtn = ({ projectid, filename, format, disabled }) => {
  const [loading, setLoading] = useState(false);
  const meta = FORMAT_META[format];
  return (
    <button
      className={`button is-small ${loading ? "is-loading" : ""}`}
      disabled={disabled || loading}
      onClick={async () => {
        setLoading(true);
        try { await downloadFile(projectid, filename, format); }
        catch (e) { console.error("Download failed:", e); }
        setLoading(false);
      }}
      title={`Download as ${meta.label}`}
      style={{ borderRadius: "5px", minWidth: "34px", padding: "0 0.5em" }}
    >
      <span className="icon" style={{ fontSize: "0.75rem", color: meta.color }}>
        <i className={meta.icon} />
      </span>
    </button>
  );
};

// Section header
const SectionHeader = ({ icon, title }) => (
  <div style={{
    display: "flex", alignItems: "center", gap: "0.5rem",
    padding: "0.5rem 0", borderBottom: "2px solid var(--ev-border)", marginBottom: "0.75rem",
  }}>
    <i className={icon} style={{ color: "var(--ev-purple)", fontSize: "1rem" }} />
    <span style={{ fontWeight: 700, fontSize: "0.95rem" }}>{title}</span>
  </div>
);

const Downloads = () => {
  const project = useSelector((state) => state.project);
  const [downloadingXlsx, setDownloadingXlsx] = useState(false);
  const [generatingPptx, setGeneratingPptx] = useState(false);

  if (!project.hasMaster)
    return (
      <div className="notification is-warning is-light">
        No master frame has been built yet.
      </div>
    );

  return (
    <div>
      {/* ── Master Data ── */}
      <SectionHeader icon="fa-duotone fa-layer-group" title="Master Data" />
      <div className="table-container" style={{ marginBottom: "2rem" }}>
        <table className="table is-fullwidth is-striped" style={{ fontSize: "0.82rem" }}>
          <thead>
            <tr>
              <th>File</th>
              <th style={{ width: "120px", textAlign: "center" }}>CSV</th>
              <th style={{ width: "120px", textAlign: "center" }}>Excel</th>
              <th style={{ width: "120px", textAlign: "center" }}>Parquet</th>
            </tr>
          </thead>
          <tbody>
            {MASTER_FILES.map((f) => {
              const available = project[f.key];
              return (
                <tr key={f.key}>
                  <td>
                    <div style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
                      <i className="fa-duotone fa-database" style={{ color: "var(--ev-purple)", fontSize: "0.8rem" }} />
                      <span style={{ fontWeight: 600 }}>{f.label}</span>
                      {!available && (
                        <span className="tag is-light" style={{ fontSize: "0.6rem", marginLeft: "0.3rem" }}>
                          not built
                        </span>
                      )}
                    </div>
                  </td>
                  <td style={{ textAlign: "center" }}>
                    <DlBtn projectid={project.id} filename={f.filename} format="csv" disabled={!available} />
                  </td>
                  <td style={{ textAlign: "center" }}>
                    <DlBtn projectid={project.id} filename={f.filename} format="xlsx" disabled={!available} />
                  </td>
                  <td style={{ textAlign: "center" }}>
                    <DlBtn projectid={project.id} filename={f.filename} format="parquet" disabled={!available} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* ── Analysis Files ── */}
      <SectionHeader icon="fa-duotone fa-chart-mixed" title="Analysis" />
      <div className="table-container" style={{ marginBottom: "2rem" }}>
        <table className="table is-fullwidth is-striped" style={{ fontSize: "0.82rem" }}>
          <thead>
            <tr>
              <th>Analysis</th>
              <th style={{ width: "120px", textAlign: "center" }}>CSV</th>
              <th style={{ width: "120px", textAlign: "center" }}>Excel</th>
              <th style={{ width: "120px", textAlign: "center" }}>Parquet</th>
            </tr>
          </thead>
          <tbody>
            {ANALYSIS_FILES.map((f) => {
              const available = project[f.key];
              return (
                <tr key={f.key}>
                  <td>
                    <div style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
                      <i className={f.icon} style={{ color: "var(--ev-purple)", fontSize: "0.8rem" }} />
                      <span style={{ fontWeight: 600 }}>{f.label}</span>
                      {!available && (
                        <span className="tag is-light" style={{ fontSize: "0.6rem", marginLeft: "0.3rem" }}>
                          not computed
                        </span>
                      )}
                    </div>
                  </td>
                  <td style={{ textAlign: "center" }}>
                    <DlBtn projectid={project.id} filename={f.filename} format="csv" disabled={!available} />
                  </td>
                  <td style={{ textAlign: "center" }}>
                    <DlBtn projectid={project.id} filename={f.filename} format="xlsx" disabled={!available} />
                  </td>
                  <td style={{ textAlign: "center" }}>
                    <DlBtn projectid={project.id} filename={f.filename} format="parquet" disabled={!available} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* ── Bulk & Report ── */}
      <SectionHeader icon="fa-duotone fa-box-archive" title="Bulk Export & Report" />
      <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap" }}>
        {/* Combined XLSX workbook */}
        <div className="box" style={{
          flex: "1 1 220px", display: "flex", flexDirection: "column",
          alignItems: "center", gap: "0.6rem", padding: "1.25rem",
        }}>
          <i className="fa-duotone fa-file-excel" style={{ fontSize: "2rem", color: "#1B7A4A" }} />
          <span style={{ fontWeight: 700, fontSize: "0.85rem" }}>Combined Workbook</span>
          <span style={{ fontSize: "0.72rem", color: "var(--ev-text-muted)", textAlign: "center" }}>
            All analysis tables in a single Excel workbook, one sheet per analysis.
          </span>
          {downloadingXlsx && (
            <span style={{ fontSize: "0.7rem", color: "var(--ev-purple)", fontStyle: "italic", textAlign: "center" }}>
              Everette is packaging all of the analysis files. This can take a while depending on the size of the deal.
            </span>
          )}
          <button
            className={`button is-small ${downloadingXlsx ? "is-loading" : ""}`}
            disabled={downloadingXlsx}
            onClick={async () => {
              setDownloadingXlsx(true);
              try { await downloadXlsx(project.id); }
              catch (e) { console.error("XLSX download failed:", e); }
              setDownloadingXlsx(false);
            }}
            style={{ borderRadius: "6px", marginTop: "auto" }}
          >
            <span className="icon" style={{ fontSize: "0.8rem" }}>
              <i className="fa-duotone fa-download" />
            </span>
            <span>Download XLSX</span>
          </button>
        </div>

        {/* PPTX report */}
        <div className="box" style={{
          flex: "1 1 220px", display: "flex", flexDirection: "column",
          alignItems: "center", gap: "0.6rem", padding: "1.25rem",
        }}>
          <i className="fa-duotone fa-file-powerpoint" style={{ fontSize: "2rem", color: "#D64545" }} />
          <span style={{ fontWeight: 700, fontSize: "0.85rem" }}>Executive Report</span>
          <span style={{ fontSize: "0.72rem", color: "var(--ev-text-muted)", textAlign: "center" }}>
            Executive analysis report as a presentation with charts and narrative.
          </span>
          <button
            className={`button is-small ${generatingPptx ? "is-loading" : ""}`}
            disabled={!project.savedReport?.data || generatingPptx}
            onClick={async () => {
              setGeneratingPptx(true);
              try { await downloadPptx(project.id, project.savedReport.data); }
              catch (e) { console.error("PPTX download failed:", e); }
              setGeneratingPptx(false);
            }}
            style={{ borderRadius: "6px", marginTop: "auto" }}
          >
            <span className="icon" style={{ fontSize: "0.8rem" }}>
              <i className="fa-duotone fa-download" />
            </span>
            <span>Download PPTX</span>
          </button>
          {!project.savedReport?.data && (
            <span style={{ fontSize: "0.65rem", color: "var(--ev-text-muted)", fontStyle: "italic" }}>
              Generate a report first
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default Downloads;
